import { FC, memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectFeeTier } from 'state/manage/selectors'
import { setFeeTier } from 'state/manage/reducer'
import { SectionHeader, SectionTitle } from 'sections/market/mobile'
import Connector from 'containers/Connector'
import { FEE_TIERS } from '@bitly/sdk/constants'

const MobileFeeTierSelector: FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const feeTier = useAppSelector(selectFeeTier)
	const { network } = Connector.useContainer()

	const activeFeeTiers = FEE_TIERS[network?.id?.toString()!] || []

	const handleSelectTier = useCallback(
		(tier: number) => {
			dispatch(setFeeTier(tier))
		},
		[dispatch]
	)

	return (
		<div>
			<SectionHeader>
				<SectionTitle>
					{t('manage.list-pair.modal.fee-tier', 'Fee Tier')}
				</SectionTitle>
			</SectionHeader>
			<TiersContainer>
				{activeFeeTiers.map((tier: number) => (
					<TierOption
						key={tier}
						active={tier === feeTier}
						onClick={() => handleSelectTier(tier)}
					>
						{tier / 10000}%
					</TierOption>
				))}
				{activeFeeTiers.length === 0 && (
					<NoTiersText>No fee tiers available for this network.</NoTiersText>
				)}
			</TiersContainer>
		</div>
	)
})

const TiersContainer = styled.div`
	display: flex;
	justify-content: flex-start;
	gap: 10px;
	flex-wrap: wrap;
	margin-bottom: 15px;
`

const TierOption = styled.div<{ active: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px 16px;
	border-radius: 8px;
	font-size: 15px;
	font-family: ${(props) => props.theme.fonts.bold};
	cursor: pointer;
	border: 1px solid
		${(props) =>
			props.active ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.border};
	background: ${(props) =>
		props.active ? props.theme.colors.selectedTheme.button.fillHover : 'transparent'};
	color: ${(props) =>
		props.active
			? props.theme.colors.selectedTheme.button.text.primary
			: props.theme.colors.selectedTheme.text.value};
	transition: all 0.2s ease-in-out;

	&:hover {
		border-color: ${(props) => props.theme.colors.selectedTheme.yellow};
	}
`

const NoTiersText = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	font-size: 14px;
`

export default MobileFeeTierSelector
